import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import Lenis from "lenis";

type LenisContextValue = {
  scrollTo: (target: string | number | HTMLElement) => void;
};

const LenisContext = createContext<LenisContextValue>({
  scrollTo: () => undefined,
});

export const useLenis = (): LenisContextValue => useContext(LenisContext);

const NAV_OFFSET = -80;

const resolveTarget = (
  target: string | number | HTMLElement
): HTMLElement | number | null => {
  if (typeof target === "number") {
    return target;
  }

  if (typeof target === "string") {
    return document.querySelector<HTMLElement>(target);
  }

  return target;
};

type LenisProviderProps = {
  children: ReactNode;
};

export const LenisProvider = ({ children }: LenisProviderProps) => {
  const lenisRef = useRef<Lenis | null>(null);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion) {
      return undefined;
    }

    const lenis = new Lenis({
      duration: 1.2,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      autoRaf: true,
    });

    lenisRef.current = lenis;

    return () => {
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  const scrollTo = useCallback((target: string | number | HTMLElement) => {
    const resolved = resolveTarget(target);

    if (lenisRef.current) {
      if (resolved instanceof HTMLElement) {
        lenisRef.current.scrollTo(resolved, { offset: NAV_OFFSET });
        return;
      }

      if (typeof resolved === "number") {
        lenisRef.current.scrollTo(resolved, { offset: NAV_OFFSET });
        return;
      }

      return;
    }

    if (resolved instanceof HTMLElement) {
      resolved.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    if (typeof resolved === "number") {
      window.scrollTo({ top: resolved, behavior: "smooth" });
    }
  }, []);

  return (
    <LenisContext.Provider value={{ scrollTo }}>
      {children}
    </LenisContext.Provider>
  );
};
